import Box from "@mui/material/Box";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import CustomerUserEntity from "../../../API/customer/CustomerUserEntity";
import AddressCustomerService from "../../../API/customer/AddressCustomerService";
import CustomerService from "../../../API/customer/CustomerService";
import CircularProgress from "@mui/material/CircularProgress";
import {TextField, Typography, useMediaQuery} from "@mui/material";
import OrderDatePicker from "../../../components/UI/datepicker/OrderDatePicker";
import dayjs from "dayjs";
import {replaceEmptyStringsOnNull} from "../../../utils/ReplaceEmptyStringsOnNull";
import {CUSTOMER_PASSPORT_FIELD_DEFAULTS} from "../../../data/Personalization";
import {countryOptions} from "../../../data/Country";
import AddressModal from "../../../components/UI/table/settings/AddressModal";
import {
    addressRawToFormState,
    formatAddressValue,
    getAddressFieldLabel,
    getAddressTypeForFieldKey,
    getEmptyAddressFormState,
    hasAddressContent,
} from "../../../utils/customerAddressUtils";
import {OrderAddressChooseModal, OrderAddressSelectModal} from "./OrderAddressFlowModals";
import {
    compositionMediaModulePanelSx,
    mediaModuleSectionColumnsSx,
    mediaModuleSectionRowSx,
    mediaModuleSectionTitleSx,
} from "../../../components/composition/CompositionMediaView";
import {compositionMediaModulePanelInCardSx} from "../../../components/composition/compositionMediaLayoutSx";
import {
    getPersonalizationFieldLabelSx,
    personalizationDatePickerWrapSx,
    personalizationFieldInputTextSx,
    personalizationFieldRowSx,
    personalizationPassportNumberFieldSx,
    personalizationPassportNumberGroupSx,
    personalizationPassportNumberRowSx,
    personalizationPassportRowSx,
    personalizationPassportSeriesFieldSx,
    personalizationPassportSeriesWrapSx,
    personalizationPassportValueRowSx,
    personalizationFieldsNarrowMediaQuery,
    personalizationReadOnlyValueSx,
    personalizationReadOnlyValueTextSx,
    personalizationTextFieldSx,
    personalizationAddressSectionSx,
} from "./orderPersonalizationFieldLayout";
import {
    buildPassportPayload,
    combinePassport,
    parsePassport,
    PASSPORT_NUMBER_PLACEHOLDER,
    PASSPORT_SERIES_PLACEHOLDER,
    sanitizePassportNumber,
    sanitizePassportSeries,
} from "../../../utils/customerPassportUtils";

const personalizationPlaceholders = {
    lastName: "Введите фамилию",
    firstName: "Введите имя",
    middleName: "Введите отчество",
    birthday: "Укажите дату рождения",
    phoneNumber: "Введите номер телефона",
    whoIssuedPassport: "Укажите, кем выдан",
    dateOfIssuePassport: "Укажите дату выдачи",
    address: "Введите адрес проживания",
    residentialAddress: "Введите адрес доставки",
    passportSeries: PASSPORT_SERIES_PLACEHOLDER,
    passportNumber: PASSPORT_NUMBER_PLACEHOLDER,
};

const normalizeUserData = (raw = {}) => ({
    ...CUSTOMER_PASSPORT_FIELD_DEFAULTS,
    ...raw,
    address: formatAddressValue(raw.address),
    residentialAddress: formatAddressValue(raw.residentialAddress),
    addressRaw: hasAddressContent(raw.address) ? raw.address : null,
    residentialAddressRaw: hasAddressContent(raw.residentialAddress) ? raw.residentialAddress : null,
});

const preparePersistBody = (payload) => {
    const {
        id,
        address,
        residentialAddress,
        addressRaw,
        residentialAddressRaw,
        ...rest
    } = payload;

    return {
        ...rest,
        passport: buildPassportPayload(payload.passport),
    };
};

const getFieldValue = (data, key) => {
    const value = data?.[key];
    return value == null ? "" : String(value);
};

const OrderCustomerPersonalizationCard = ({handleModalAlert}) => {
    const leftFields = useMemo(() => [
        {key: "lastName", label: "Фамилия"},
        {key: "firstName", label: "Имя"},
        {key: "middleName", label: "Отчество"},
        {key: "birthday", label: "Дата рождения", type: "date"},
    ], []);

    const rightFields = useMemo(() => [
        {key: "phoneNumber", label: "Номер телефона", type: "tel"},
    ], []);

    const passportFields = useMemo(() => [
        {key: "whoIssuedPassport", label: "Кем выдан"},
        {key: "dateOfIssuePassport", label: "Дата выдачи", type: "date"},
    ], []);

    const addressFields = useMemo(() => [
        {key: "address", label: "Адрес проживания"},
        {key: "residentialAddress", label: "Адрес доставки"},
    ], []);

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [addressFlowStep, setAddressFlowStep] = useState(null);
    const [addressFormMode, setAddressFormMode] = useState("create");
    const [addressModalFieldKey, setAddressModalFieldKey] = useState("address");
    const [addressFormState, setAddressFormState] = useState(getEmptyAddressFormState);
    const [addressList, setAddressList] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState("");
    const [addressModalLoading, setAddressModalLoading] = useState(false);
    const allCountry = useMemo(() => countryOptions, []);
    const dataRef = useRef(data);
    const editStartRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const skipBlurCommitRef = useRef(false);

    dataRef.current = data;

    const handleModalAlertRef = useRef(handleModalAlert);
    handleModalAlertRef.current = handleModalAlert;

    const passportParts = parsePassport(data.passport);
    const isPassportNumberOnSeparateRow = useMediaQuery(personalizationFieldsNarrowMediaQuery);

    useEffect(() => {
        let cancelled = false;

        CustomerUserEntity.getOwnForComposition()
            .then((response) => {
                if (!cancelled) {
                    setData(normalizeUserData(response.data));
                }
            })
            .catch((error) => handleModalAlertRef.current(error))
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const reloadUserData = useCallback(async () => {
        const response = await CustomerUserEntity.getOwnForComposition();
        setData(normalizeUserData(response.data));
    }, []);

    const persistData = useCallback(async (payload, {rollback, fieldKey} = {}) => {
        const body = preparePersistBody(payload);
        setSaving(true);
        try {
            await CustomerUserEntity.edit(replaceEmptyStringsOnNull(body));
            await reloadUserData();
            handleModalAlert("Данные изменены", "success");
        } catch (error) {
            if (rollback) {
                setData((prev) => rollback(prev));
            } else if (fieldKey != null) {
                setData((prev) => ({
                    ...prev,
                    [fieldKey]: editStartRef.current?.value ?? "",
                }));
            }
            handleModalAlert(error);
        } finally {
            setSaving(false);
            editStartRef.current = null;
        }
    }, [handleModalAlert, reloadUserData]);

    const commitFieldEdit = useCallback(async (fieldKey) => {
        const editStart = editStartRef.current;
        if (!editStart || editStart.field !== fieldKey || saving) {
            return;
        }

        const currentValue = getFieldValue(dataRef.current, fieldKey);
        const previousValue = editStart.value ?? "";
        if (currentValue === previousValue) {
            editStartRef.current = null;
            return;
        }

        await persistData(dataRef.current, {fieldKey});
    }, [persistData, saving]);

    const handleFieldFocus = (fieldKey, value) => {
        if (saving) {
            return;
        }
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        editStartRef.current = {
            field: fieldKey,
            value: value ?? getFieldValue(dataRef.current, fieldKey),
        };
    };

    const handleFieldBlur = (fieldKey) => {
        if (saving) {
            return;
        }
        if (skipBlurCommitRef.current) {
            skipBlurCommitRef.current = false;
            return;
        }
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }
        blurTimeoutRef.current = window.setTimeout(() => {
            blurTimeoutRef.current = null;
            commitFieldEdit(fieldKey);
        }, 0);
    };

    const handleFieldKeyDown = (fieldKey, event) => {
        if (event.key !== "Enter") {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        skipBlurCommitRef.current = true;
        commitFieldEdit(fieldKey);
        event.currentTarget.blur();
    };

    const handleFieldChange = (fieldKey, value) => {
        setData((prev) => ({
            ...prev,
            [fieldKey]: value,
        }));
    };

    const handleDateFieldChange = (fieldKey) => (nextDate) => {
        if (!editStartRef.current || editStartRef.current.field !== fieldKey) {
            editStartRef.current = {
                field: fieldKey,
                value: getFieldValue(dataRef.current, fieldKey),
            };
        }

        const nextValue = nextDate ? dayjs(nextDate).format("YYYY-MM-DD") : "";
        const previousValue = editStartRef.current.value ?? "";

        setData((prev) => ({
            ...prev,
            [fieldKey]: nextValue,
        }));

        if (nextValue === previousValue || saving) {
            editStartRef.current = null;
            return;
        }

        persistData({
            ...dataRef.current,
            [fieldKey]: nextValue,
        }, {
            rollback: (prev) => ({
                ...prev,
                [fieldKey]: previousValue,
            }),
        });
    };

    const getCurrentAddressRaw = useCallback((fieldKey = addressModalFieldKey) => (
        fieldKey === "address" ? dataRef.current.addressRaw : dataRef.current.residentialAddressRaw
    ), [addressModalFieldKey]);

    const closeAddressFlow = useCallback(() => {
        setAddressFlowStep(null);
        setAddressFormMode("create");
        setAddressFormState(getEmptyAddressFormState());
        setAddressList([]);
        setSelectedAddressId("");
    }, []);

    const openAddressForm = useCallback((mode, raw = null) => {
        setAddressFormMode(mode);
        setAddressFormState(
            mode === "create" ? getEmptyAddressFormState() : addressRawToFormState(raw),
        );
        setAddressFlowStep("form");
    }, []);

    const handleOpenAddressFlow = async (fieldKey) => {
        if (saving || addressModalLoading) {
            return;
        }

        setAddressModalFieldKey(fieldKey);
        setAddressModalLoading(true);
        try {
            const response = await AddressCustomerService.getAll();
            const list = response.data ?? [];
            setAddressList(list);

            const currentRaw = fieldKey === "address"
                ? dataRef.current.addressRaw
                : dataRef.current.residentialAddressRaw;
            const currentId = hasAddressContent(currentRaw) ? String(currentRaw.id) : "";
            setSelectedAddressId(currentId || (list[0]?.id != null ? String(list[0].id) : ""));
            setAddressFlowStep("choose");
        } catch (error) {
            handleModalAlert(error);
        } finally {
            setAddressModalLoading(false);
        }
    };

    const handleLinkSelectedAddress = async () => {
        if (!selectedAddressId) {
            return;
        }

        setAddressModalLoading(true);
        try {
            await CustomerService.updateAddress(
                getAddressTypeForFieldKey(addressModalFieldKey),
                Number(selectedAddressId),
            );
            await reloadUserData();
            handleModalAlert("Адрес сохранён", "success");
            closeAddressFlow();
        } catch (error) {
            handleModalAlert(error);
        } finally {
            setAddressModalLoading(false);
        }
    };

    const handleAddressSubmit = async (formState) => {
        setAddressModalLoading(true);
        try {
            if (addressFormMode === "create") {
                const {id, ...addressBody} = formState;
                await AddressCustomerService.createAndSet(
                    getAddressTypeForFieldKey(addressModalFieldKey),
                    replaceEmptyStringsOnNull({id: null, ...addressBody}),
                );
            } else {
                const {id, ...addressBody} = formState;
                await AddressCustomerService.update(
                    replaceEmptyStringsOnNull(addressBody),
                    id,
                );
            }
            await reloadUserData();
            handleModalAlert("Адрес сохранён", "success");
            closeAddressFlow();
        } catch (error) {
            handleModalAlert(error);
        } finally {
            setAddressModalLoading(false);
        }
    };

    const handleEditSelectedAddress = () => {
        const selectedAddress = addressList.find(
            (item) => String(item.id) === String(selectedAddressId),
        );
        if (!selectedAddress) {
            return;
        }
        openAddressForm("edit", selectedAddress);
    };

    const handlePassportPartChange = (part, value) => {
        const current = parsePassport(dataRef.current.passport);
        const sanitizedValue = part === "series"
            ? sanitizePassportSeries(value)
            : sanitizePassportNumber(value);
        const nextPassport = combinePassport(
            part === "series" ? sanitizedValue : current.series,
            part === "number" ? sanitizedValue : current.number,
        );
        setData((prev) => ({
            ...prev,
            passport: nextPassport,
        }));
    };

    const handlePassportFocus = () => {
        handleFieldFocus("passport", dataRef.current.passport ?? "");
    };

    const handlePassportBlur = () => {
        handleFieldBlur("passport");
    };

    const handlePassportKeyDown = (event) => {
        handleFieldKeyDown("passport", event);
    };

    const renderPassportNumberField = () => (
        <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={passportParts.number}
            placeholder={personalizationPlaceholders.passportNumber}
            disabled={saving}
            onChange={(event) => handlePassportPartChange("number", event.target.value)}
            onFocus={handlePassportFocus}
            onBlur={handlePassportBlur}
            onKeyDown={handlePassportKeyDown}
            sx={personalizationPassportNumberFieldSx}
            inputProps={{
                "aria-label": "Номер паспорта",
                inputMode: "numeric",
                pattern: "[0-9]*",
            }}
        />
    );

    const renderPassportRow = () => (
        <>
            <Box
                sx={{
                    ...personalizationPassportRowSx,
                    ...(!saving ? {cursor: "text"} : {}),
                }}
            >
                <Typography sx={getPersonalizationFieldLabelSx("right")}>Паспорт, серия</Typography>
                <Box sx={personalizationPassportValueRowSx}>
                    <Box sx={personalizationPassportSeriesWrapSx}>
                        <TextField
                            size="small"
                            variant="outlined"
                            value={passportParts.series}
                            placeholder={personalizationPlaceholders.passportSeries}
                            disabled={saving}
                            onChange={(event) => handlePassportPartChange("series", event.target.value)}
                            onFocus={handlePassportFocus}
                            onBlur={handlePassportBlur}
                            onKeyDown={handlePassportKeyDown}
                            sx={personalizationPassportSeriesFieldSx}
                            inputProps={{
                                "aria-label": "Паспорт, серия",
                                maxLength: 4,
                                inputMode: "text",
                            }}
                        />
                    </Box>
                    {!isPassportNumberOnSeparateRow && (
                        <Box sx={personalizationPassportNumberGroupSx}>
                            <Typography sx={{flexShrink: 0, ...personalizationFieldInputTextSx, fontWeight: 600}}>
                                №
                            </Typography>
                            {renderPassportNumberField()}
                        </Box>
                    )}
                </Box>
            </Box>
            {isPassportNumberOnSeparateRow && (
                <Box
                    sx={{
                        ...personalizationPassportNumberRowSx,
                        ...(!saving ? {cursor: "text"} : {}),
                    }}
                >
                    <Typography sx={getPersonalizationFieldLabelSx("right")}>№</Typography>
                    {renderPassportNumberField()}
                </Box>
            )}
        </>
    );

    const renderAddressFieldRow = ({key, label}, column) => {
        const value = getFieldValue(data, key);
        const isPlaceholder = !value;

        return (
            <Box
                key={key}
                role="button"
                tabIndex={saving || addressModalLoading ? -1 : 0}
                onClick={() => handleOpenAddressFlow(key)}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleOpenAddressFlow(key);
                    }
                }}
                sx={{
                    ...personalizationFieldRowSx,
                    ...(!saving && !addressModalLoading ? {cursor: "pointer"} : {}),
                }}
            >
                <Typography sx={getPersonalizationFieldLabelSx(column)}>{label}</Typography>
                <Box
                    sx={personalizationReadOnlyValueSx}
                    aria-label={label}
                >
                    <Typography
                        component="div"
                        sx={{
                            ...personalizationReadOnlyValueTextSx,
                            opacity: isPlaceholder ? 0.72 : 1,
                        }}
                    >
                        {isPlaceholder ? personalizationPlaceholders[key] : value}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderFieldRow = ({key, label, type, disabled}, column) => {
        return (
        <Box
            key={key}
            sx={{
                ...personalizationFieldRowSx,
                ...(!disabled && !saving ? {cursor: "text"} : {}),
            }}
        >
            <Typography sx={getPersonalizationFieldLabelSx(column)}>{label}</Typography>
            {type === "date" ? (
                <Box sx={personalizationDatePickerWrapSx}>
                    <OrderDatePicker
                        label=""
                        placeholder={personalizationPlaceholders[key]}
                        margin="none"
                        height={24}
                        date={getFieldValue(data, key)}
                        setDate={handleDateFieldChange(key)}
                    />
                </Box>
            ) : (
                <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={8}
                    size="small"
                    variant="outlined"
                    {...(type && type !== "tel" ? {type} : {})}
                    value={getFieldValue(data, key)}
                    placeholder={personalizationPlaceholders[key]}
                    disabled={disabled || saving}
                    onChange={(event) => handleFieldChange(key, event.target.value)}
                    onFocus={() => handleFieldFocus(key)}
                    onBlur={() => handleFieldBlur(key)}
                    onKeyDown={(event) => handleFieldKeyDown(key, event)}
                    sx={personalizationTextFieldSx}
                    inputProps={{
                        "aria-label": label,
                    }}
                />
            )}
        </Box>
        );
    };

    const addressModalTitle = getAddressFieldLabel(addressModalFieldKey);
    const hasCurrentAddress = hasAddressContent(getCurrentAddressRaw());

    if (loading) {
        return (
            <Box
                sx={{
                    ...compositionMediaModulePanelSx,
                    ...compositionMediaModulePanelInCardSx,
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "200px",
                }}
            >
                <CircularProgress size={28}/>
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    ...compositionMediaModulePanelSx,
                    ...compositionMediaModulePanelInCardSx,
                }}
            >
                <Typography sx={mediaModuleSectionTitleSx}>Реквизиты покупателя</Typography>
                <Box sx={{...mediaModuleSectionRowSx, mt: "16px"}}>
                    <Box sx={mediaModuleSectionColumnsSx}>
                        {leftFields.map((field) => renderFieldRow(field, "left"))}
                    </Box>
                    <Box sx={mediaModuleSectionColumnsSx}>
                        {rightFields.map((field) => renderFieldRow(field, "right"))}
                        {renderPassportRow()}
                        {passportFields.map((field) => renderFieldRow(field, "right"))}
                    </Box>
                </Box>
                <Box sx={personalizationAddressSectionSx}>
                    {addressFields.map((field) => renderAddressFieldRow(field, "right"))}
                </Box>
            </Box>
            <OrderAddressChooseModal
                open={addressFlowStep === "choose"}
                title={addressModalTitle}
                hasCurrentAddress={hasCurrentAddress}
                hasExistingAddresses={addressList.length > 0}
                loading={addressModalLoading}
                onClose={closeAddressFlow}
                onSelectExisting={() => setAddressFlowStep("select")}
                onCreateNew={() => openAddressForm("create")}
                onEditCurrent={() => openAddressForm("edit", getCurrentAddressRaw())}
            />
            <OrderAddressSelectModal
                open={addressFlowStep === "select"}
                title={addressModalTitle}
                addressList={addressList}
                selectedAddressId={selectedAddressId}
                loading={addressModalLoading}
                onClose={closeAddressFlow}
                onSelectedAddressIdChange={setSelectedAddressId}
                onConfirm={handleLinkSelectedAddress}
                onEditSelected={handleEditSelectedAddress}
                onCreateNew={() => openAddressForm("create")}
            />
            <AddressModal
                open={addressFlowStep === "form"}
                onClose={closeAddressFlow}
                handleSubmit={handleAddressSubmit}
                initValue={addressFormState}
                title={addressFormMode === "create" ? `Создать: ${addressModalTitle}` : `Редактировать: ${addressModalTitle}`}
                allCountry={allCountry}
                loading={addressModalLoading}
                submitButtonText="Сохранить"
                fullWidth
            />
        </>
    );
};

export default OrderCustomerPersonalizationCard;
